import { Controller } from "@hotwired/stimulus"

// "How to use PCP Maker" — a two-pane illustrated manual (icon menu on the
// left, rich detail on the right). Opened from the sidebar Help link; the
// dialog shell lives in the sidebar partial and this controller renders + runs
// the manual into [root]. Closes on the × button, Escape (native <dialog>), or
// a backdrop click.
const ICON = {
  book:'<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>',
  panes:'<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M12 4v16"/>',
  flag:'<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" x2="4" y1="22" y2="15"/>',
  scan:'<path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2"/><path d="M7 8h10M7 12h7M7 16h10"/>',
  save:'<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8M7 3v5h8"/>',
  pdf:'<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h6"/><path d="M14 2v6h6"/><circle cx="16" cy="16" r="3"/><path d="m21 21-1.5-1.5"/>',
  clip:'<path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>',
  lock:'<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>',
  help:'<circle cx="12" cy="12" r="10"/><path d="m4.93 4.93 4.24 4.24M14.83 9.17l4.24-4.24M14.83 14.83l4.24 4.24M9.17 14.83l-4.24 4.24"/><circle cx="12" cy="12" r="4"/>',
}
const svg = (p) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${p}</svg>`

const GROUPS = [
  { g: "Getting started", items: [
    { id: "overview", ic: "book", grad: "linear-gradient(135deg,#6366f1,#8b5cf6)",
      title: "Welcome to PCP Maker", sub: "Build a Person-Centered Plan, section by section.",
      fig: `<div class="hg-pane">
              <div class="p"><div class="bar"></div><div class="ln" style="width:60%"></div><div class="ln" style="width:80%"></div><div class="ln" style="width:45%"></div></div>
              <div class="p wiz"><div class="bar"></div><div class="ln" style="width:70%"></div><div class="ln" style="width:55%"></div><div class="ln" style="width:75%"></div></div>
            </div>`,
      cap: "Editor (left) and Wizard (right) — two views of the same plan.",
      body: `<p>PCP Maker walks you through a complete Person-Centered Plan. Your work <b>saves automatically in this browser</b> as you type — there's no "save" to keep going.</p>
             <h3>The big idea</h3>
             <p>Move through the plan using the <b>section list</b> on the left. Fill each section in the <b>Editor</b>, or answer friendly guided questions in the <b>Wizard</b> — they edit the same data.</p>
             <div class="hg-tip">${svg(ICON.help)} New here? Start at <b>Client</b> and work down. Leave a <b>flag</b> on anything you're unsure of and come back to it.</div>` },
    { id: "panes", ic: "panes", grad: "linear-gradient(135deg,#0891b2,#2563eb)",
      title: "Editor & Wizard", sub: "Two panes, one plan — show either or both.",
      fig: `<span class="hg-tag blue">Editor</span><span class="hg-tag blue">Wizard</span>`,
      cap: "Toggle each pane from the toolbar switches.",
      body: `<h3>Editor (left)</h3><p>The full plan — type directly into any field.</p>
             <h3>Wizard (right)</h3><p>The same fields as guided questions with quick-pick chips. Great for getting started or filling gaps.</p>
             <ul class="hg-steps"><li><span class="hg-n">1</span> Use the <b>Editor</b> / <b>Wizard</b> switches in the toolbar.</li>
             <li><span class="hg-n">2</span> Show one for focus, or both side-by-side to cross-check.</li></ul>` },
  ]},
  { g: "Working on the plan", items: [
    { id: "flags", ic: "flag", grad: "linear-gradient(135deg,#f59e0b,#d97706)",
      title: "Flags & open questions", sub: "Mark anything to be determined and review it later.",
      fig: `<div style="display:flex;flex-direction:column;gap:14px;align-items:center">
              <div style="display:flex;align-items:center;gap:12px"><span class="hg-field">Date of birth</span><span class="hg-flag">${svg(ICON.flag)} FLAGGED</span></div>
              <div class="hg-flag">${svg(ICON.flag)} 3 to review</div>
            </div>`,
      cap: "Flag a field → it's counted in the toolbar's flag pill.",
      body: `<ul class="hg-steps">
               <li><span class="hg-n">1</span> Click the <b>flag</b> beside any field to mark it <em>to be determined</em>.</li>
               <li><span class="hg-n">2</span> Or type <kbd>TBD</kbd> / <kbd>??</kbd> in a field to leave an open question.</li>
               <li><span class="hg-n">3</span> The <b>flag counter</b> in the toolbar shows how many are open — click it to review them all in <b>Open Questions</b>.</li>
             </ul>
             <div class="hg-tip">${svg(ICON.help)} Flagging never erases your text — it's just a marker you can clear anytime.</div>` },
    { id: "templatize", ic: "scan", grad: "linear-gradient(135deg,#6366f1,#4f46e5)",
      title: "Templatize", sub: "Swap names & age for reusable tokens.",
      fig: `<div style="display:flex;align-items:center;gap:14px">
              <span class="hg-field">John Doe, age 12</span><span style="color:#6b7c96">→</span>
              <span class="hg-field"><kbd>[FirstName]</kbd> <kbd>[LastName]</kbd>, age <kbd>[Age]</kbd></span>
            </div>`,
      cap: "One click turns a real plan into a reusable template.",
      body: `<p>Templatize scans the plan for the participant's name and age and replaces each with <kbd>[FirstName]</kbd> / <kbd>[LastName]</kbd> / <kbd>[Age]</kbd> tokens — perfect for building a starter template you reuse across clients.</p>` },
  ]},
  { g: "Document tools", items: [
    { id: "redact", ic: "lock", grad: "linear-gradient(135deg,#334155,#0f172a)",
      title: "Redact · Draft · Lock", sub: "Control what shows on the printed plan.",
      fig: `<div class="hg-doc"><span class="ribbon">REDACT</span>
              <div class="l" style="width:70%"></div><div class="l red" style="width:90%"></div>
              <div class="l" style="width:60%"></div><div class="l red" style="width:50%"></div><div class="l" style="width:80%"></div>
            </div>`,
      cap: "Redaction blacks out PHI on the output.",
      body: `<ul class="hg-steps">
               <li><span class="hg-n">1</span> <b>Redact</b> — hide PHI (names, identifiers) on the PDF.</li>
               <li><span class="hg-n">2</span> <b>Draft</b> — stamp a watermark so a work-in-progress reads as draft.</li>
               <li><span class="hg-n">3</span> <b>Lock</b> — password-protect the exported PDF.</li>
             </ul>` },
    { id: "save", ic: "save", grad: "linear-gradient(135deg,#059669,#047857)",
      title: "Save & open (.sdp)", sub: "An encrypted, portable copy of the plan.",
      fig: `<span class="hg-mbtn">${svg(ICON.save)}</span>
            <span class="hg-lock">${svg(ICON.lock)}</span>
            <span class="hg-callout"><b>.sdp</b> files are AES-encrypted — safe to email or move between computers.</span>`,
      cap: "Save (.sdp) writes an encrypted file; Open (.sdp) reloads it.",
      body: `<p>Your plan auto-saves in this browser. For a portable, <b>encrypted</b> backup — or to move a plan to another computer — use <b>Save (.sdp)</b>, then <b>Open (.sdp)</b> to load it back.</p>` },
    { id: "pdf", ic: "pdf", grad: "linear-gradient(135deg,#dc2626,#b91c1c)",
      title: "Review, print & sign", sub: "Preview the finished plan and add a signature page.",
      fig: `<div class="hg-toolbar"><span class="hg-mbtn">${svg(ICON.clip)}</span><span style="color:#9fb0c8;font-size:12.5px">Review &amp; Sign Page</span><span class="hg-mbtn pri">${svg(ICON.pdf)}</span></div>`,
      cap: "Turn on the sign page, then open PDF preview.",
      body: `<ul class="hg-steps">
               <li><span class="hg-n">1</span> Turn on <b>Review &amp; Sign Page</b> to append a review / acknowledgment / signature page.</li>
               <li><span class="hg-n">2</span> Open <b>PDF preview</b> to review, print, or export the finished plan.</li>
             </ul>` },
  ]},
  { g: "Help", items: [
    { id: "issue", ic: "help", grad: "linear-gradient(135deg,#6366f1,#8b5cf6)",
      title: "Report an issue", sub: "Something not covered here?",
      fig: `<span class="hg-callout">Use <b>Report an issue</b> in the toolbar to send us a note — we read every one.</span>`,
      cap: "",
      body: `<p>Can't find what you need, or something isn't working right? Use the <b>Report an issue</b> tool in the toolbar and we'll take a look.</p>` },
  ]},
]

export default class extends Controller {
  static targets = ["dialog", "root"]

  connect() {
    this._topics = GROUPS.flatMap(s => s.items)
    if (this.hasRootTarget && !this._built) { this._build(); this._built = true }
  }

  open(event) {
    event?.preventDefault?.()
    if (this.hasDialogTarget && !this.dialogTarget.open) this.dialogTarget.showModal()
  }
  close() { if (this.hasDialogTarget && this.dialogTarget.open) this.dialogTarget.close() }
  // A click on the <dialog> element itself lands on the backdrop (the manual
  // fills the box, so inner clicks hit children).
  backdropClose(event) { if (event.target === this.dialogTarget) this.close() }

  _build() {
    const menu = GROUPS.map(sec => `
      <div class="hg-grp">${sec.g}</div>
      ${sec.items.map(t => `
        <button type="button" class="hg-item" data-topic="${t.id}">
          <span class="hg-ic">${svg(ICON[t.ic])}</span><span>${t.title}</span>
        </button>`).join("")}
    `).join("")
    this.rootTarget.innerHTML = `
      <aside class="hg-side">
        <div class="hg-side-head">
          <div class="hg-badge"><span class="hg-mark"></span> PCP Maker manual</div>
          <div class="hg-sub">Everything you need to build a plan</div>
        </div>
        <nav class="hg-nav">${menu}</nav>
      </aside>
      <section class="hg-detail">
        <button type="button" class="hg-close" aria-label="Close">&times;</button>
        <div class="hg-render"></div>
      </section>`
    // Delegated clicks: topic select + close.
    this.rootTarget.addEventListener("click", (e) => {
      const item = e.target.closest(".hg-item")
      if (item) { this._select(item.dataset.topic); return }
      if (e.target.closest(".hg-close")) this.close()
    })
    this._select(this._topics[0].id)
  }

  _select(id) {
    const t = this._topics.find(x => x.id === id) || this._topics[0]
    this.rootTarget.querySelectorAll(".hg-item").forEach(b => b.classList.toggle("is-on", b.dataset.topic === t.id))
    const render = this.rootTarget.querySelector(".hg-render")
    render.innerHTML = `
      <div class="hg-head">
        <div class="hg-hic" style="background:${t.grad}">${svg(ICON[t.ic])}</div>
        <div><h2>${t.title}</h2><p>${t.sub}</p></div>
      </div>
      <div class="hg-content">
        <div class="hg-figure"><div class="hg-stage">${t.fig}</div>${t.cap ? `<div class="hg-cap">${t.cap}</div>` : ""}</div>
        ${t.body}
      </div>`
    this.rootTarget.querySelector(".hg-detail").scrollTop = 0
  }
}
